import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { OpenInNew } from "@mui/icons-material";
import { Button, CircularProgress, Link as MuiLink, Typography, Grid, Chip, Box, Skeleton } from "@mui/material";
import axios from "axios";
import { DateTime } from "luxon";
import { getOrParseDateTime } from "@cpt/shared";
import { useFeatureFlag } from "contexts/ConfigurationProvider/ConfigurationProvider";
import { FeatureFlags } from "contexts/ConfigurationProvider/FeatureFlags";
import { ZuoraUriMap } from "utils/ZuoraUrls";
import { getErrorMessage } from "utils/errorHelpers";
import {
  formatDateAsLocaleDateString,
  formatDateAsLocaleString,
  formatDateAsTimeString,
} from "utils/formatters/DateFormatters";
import { formatPlatform } from "utils/formatters/PlatformFormatters";
import type { IsoDayOfWeek, MonthlyMaintenanceWindowWeekOfMonth } from "client/api-v2";
import {
  CloudSubscriptionStatus,
  getAutoRenewPeriodLabel,
  LifecycleType,
  MaintenanceWindowFrequency,
  ProductPlatform,
  reactivatableLifecycleTypes,
  subscriptionsApi,
  SubscriptionStatus,
  type AutoRenewPeriod,
  type BillingAccountSubscriptionDto,
  type CloudSubscriptionDto,
  type ComplimentaryType,
  type DowngradeToFreeRequestDto,
  type EntitlementConfiguration,
  type ExtendSubscriptionTermRequestDto,
  type ServerLicenseDto,
  type Subscription,
  type TransitionToComplimentaryRequestDto,
  type TransitionToEvergreenRequestDto,
  type TransitionToPaidRequestDto,
  type TransitionToTrialRequestDto,
} from "client/api-v2";
import { changeTaskCap, UnlimitedTaskCap } from "client/api/CloudSubscriptionApi";
import { useAlerts, AlertSeverity } from "components/AlertProvider/AlertProvider";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { InfoField } from "components/InfoField/InfoField";
import { Overflow } from "components/OverflowMenu/OverflowMenu";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import { useRbacContext } from "components/authorization/RbacProvider";
import { ChangeTaskCapDialog } from "components/cloud-subscriptions/ChangeTaskCapDialog";
import { Fieldset } from "components/form/Fieldset";
import { billingAccountRoutes } from "areas/billing-accounts/billing-account-routes";
import { LinkOrTransferSubscriptionDialog } from "areas/billing-accounts/components/LinkOrTransferSubscriptionDialog";
import { LicenseKeyView } from "areas/licenses/detail/LicenseKeyView";
import { CreateQuoteDialog } from "areas/orders/components/CreateQuoteDialog";
import { useOpenNewQuote } from "areas/orders/hooks/useOpenNewQuote";
import { CancelSubscriptionDialog } from "../components/CancelSubscriptionDialog";
import { ChangeMaintenanceWindowDialog } from "../components/ChangeMaintenanceWindowDialog";
import { ChangePlanDialog } from "../components/ChangePlanDialog";
import { CloudSubscriptionActions } from "../components/CloudSubscriptionActions";
import { DowngradeToFreeDialog } from "../components/DowngradeToFreeDialog";
import { EditAutoRenewalDialog } from "../components/EditAutoRenewalDialog";
import { EditEntitlementOverridesDialog } from "../components/EditEntitlementOverridesDialog";
import { EditSelfServiceDialog } from "../components/EditSelfServiceDialog";
import { EditZuoraSubscriptionKeyDialog } from "../components/EditZuoraSubscriptionKeyDialog";
import { ExtendSubscriptionTermDialog } from "../components/ExtendSubscriptionTermDialog";
import { ProtectUserAccessDialog } from "../components/ProtectUserAccessDialog";
import { ReactivateSubscriptionDialog } from "../components/ReactivateSubscriptionDialog";
import { SendLicenseEmailDialog } from "../components/SendLicenseEmailDialog";
import { ServerSubscriptionActions } from "../components/ServerSubscriptionActions";
import { TransitionToComplimentaryDialog } from "../components/TransitionToComplimentaryDialog";
import { TransitionToEvergreenDialog } from "../components/TransitionToEvergreenDialog";
import { TransitionToPaidDialog } from "../components/TransitionToPaidDialog";
import { TransitionToTrialDialog } from "../components/TransitionToTrialDialog";
import { EntitlementViewer } from "../components/shared/EntitlementViewer";
import { subscriptionStatusColor } from "../constants/subscription-status-color";
import {
  getServerLicenseBySubscriptionId,
  getCloudSubscriptionBySubscriptionId,
  SubscriptionService,
} from "../services/subscriptionService";
import { subscriptionRoutes } from "../subscription-routes";
import {
  formatEntitlementValue,
  lifecycleSupportsEntitlementOverrides,
  lifecycleSyncsTaskCapOverride,
} from "../utils/plan-utils";

type SubscriptionWithLifecycleFields = Subscription & {
  entitlementOverrides?: EntitlementConfiguration;
  autoRenewPeriod?: AutoRenewPeriod;
  complimentaryType?: ComplimentaryType;
};

function getCurrentTaskCapSelection(taskCap: unknown): string | undefined {
  if (typeof taskCap === "number") return taskCap.toString();
  if (typeof taskCap === "object" && taskCap !== null && "isUnlimited" in taskCap) {
    if (taskCap.isUnlimited) return UnlimitedTaskCap;
    if ("limit" in taskCap && typeof taskCap.limit === "number") return taskCap.limit.toString();
  }
  return undefined;
}

function getMaintenanceWindowFrequencyLabel(
  frequency: MaintenanceWindowFrequency,
  dayOfWeek?: IsoDayOfWeek,
  weekOfMonth?: MonthlyMaintenanceWindowWeekOfMonth
): string {
  if (frequency === MaintenanceWindowFrequency.Daily) return "Daily";
  if (frequency === MaintenanceWindowFrequency.Weekly) return dayOfWeek ? `${dayOfWeek} of each week` : "Weekly";
  if (frequency === MaintenanceWindowFrequency.Monthly)
    return weekOfMonth ? `${weekOfMonth} Monday of each month` : "Monthly";
  return frequency;
}

export function SubscriptionDetails() {
  const { entity: subscription, refresh } = useEntity<SubscriptionWithLifecycleFields>();
  const adoptionLedPricingEnabled = useFeatureFlag(FeatureFlags.AdoptionLedPricing);
  const navigate = useNavigate();
  const openNewQuote = useOpenNewQuote();
  const { sendNewAlert } = useAlerts();
  const [serverLicense, setServerLicense] = useState<ServerLicenseDto | null>(null);
  const [cloudSubscription, setCloudSubscription] = useState<CloudSubscriptionDto | null>(null);
  const [platformDataLoading, setPlatformDataLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [editOverridesDialogOpen, setEditOverridesDialogOpen] = useState(false);
  const [editAutoRenewalDialogOpen, setEditAutoRenewalDialogOpen] = useState(false);
  const [editZuoraKeyDialogOpen, setEditZuoraKeyDialogOpen] = useState(false);
  const [editSelfServiceDialogOpen, setEditSelfServiceDialogOpen] = useState(false);
  const [extendTermDialogOpen, setExtendTermDialogOpen] = useState(false);
  const [transitionToComplimentaryDialogOpen, setTransitionToComplimentaryDialogOpen] = useState(false);
  const [transitionToTrialDialogOpen, setTransitionToTrialDialogOpen] = useState(false);
  const [transitionToEvergreenDialogOpen, setTransitionToEvergreenDialogOpen] = useState(false);
  const [transitionToPaidDialogOpen, setTransitionToPaidDialogOpen] = useState(false);
  const [downgradeToFreeDialogOpen, setDowngradeToFreeDialogOpen] = useState(false);
  const [createQuoteDialogOpen, setCreateQuoteDialogOpen] = useState(false);
  const [changeMaintenanceWindowOpen, setChangeMaintenanceWindowOpen] = useState(false);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [changeTaskCapDialogOpen, setChangeTaskCapDialogOpen] = useState(false);
  const [protectUserAccessDialogOpen, setProtectUserAccessDialogOpen] = useState(false);
  const [licenseRefreshKey, setLicenseRefreshKey] = useState(0);
  const [sendLicenseEmailDialogOpen, setSendLicenseEmailDialogOpen] = useState(false);

  const isServerPlatform = subscription?.plan?.productPlatform === "OctopusServer";
  const isCloudPlatform = subscription?.plan?.productPlatform === "OctopusCloud";
  const { isAuthorized } = useRbacContext();
  const [linkBillingAccountDialogOpen, setLinkBillingAccountDialogOpen] = useState(false);

  const applicableEntitlementKeys = useMemo(
    () => subscription?.applicableEntitlementOverrideKeys ?? [],
    [subscription]
  );

  const hasEntitlementOverrides = useMemo(() => {
    const hasJsonOverrides = Object.values(subscription?.entitlementOverrides ?? {}).some(
      (value) => value !== null && value !== undefined
    );
    const hasCloudOverrides = [
      cloudSubscription?.staffSpecifiedTaskCap,
      cloudSubscription?.customerSpecifiedMachineLimit,
    ].some((value) => value !== null && value !== undefined);
    return hasJsonOverrides || hasCloudOverrides;
  }, [subscription, cloudSubscription]);

  const refreshCloudSubscription = useCallback((subscriptionId: string) => {
    setPlatformDataLoading(true);
    getCloudSubscriptionBySubscriptionId(subscriptionId)
      .then((cloudSub) => setCloudSubscription(cloudSub))
      .catch(() => {})
      .finally(() => setPlatformDataLoading(false));
  }, []);

  useEffect(() => {
    if (!subscription?.id) {
      setPlatformDataLoading(false);
      return;
    }

    setPlatformDataLoading(true);

    if (isServerPlatform) {
      const fetchServerLicenseWithRetry = async () => {
        const maxAttempts = 5;
        const retryDelays = [2000, 4000, 6000, 8000, 10000];

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          try {
            const license = await getServerLicenseBySubscriptionId(subscription.id);

            if (license) {
              setServerLicense(license);
              return;
            }

            if (attempt < maxAttempts - 1) {
              await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
            }
          } catch (error) {
            const is404 = axios.isAxiosError(error) && error.response?.status === 404;

            if (!is404) {
              throw error;
            }

            if (attempt < maxAttempts - 1) {
              await new Promise((resolve) => setTimeout(resolve, retryDelays[attempt]));
            }
          }
        }
      };

      fetchServerLicenseWithRetry().finally(() => setPlatformDataLoading(false));
    } else if (isCloudPlatform) {
      refreshCloudSubscription(subscription.id);
    } else {
      setPlatformDataLoading(false);
    }
  }, [subscription?.id, isServerPlatform, isCloudPlatform]);

  const handleCancelSubscription = async (reason: string) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await SubscriptionService.cancelSubscription(subscription.id, reason);

    if (refresh) {
      await refresh();
    }
  };

  const handleReactivateSubscription = useCallback(
    async (newTermStart: string, newTermEnd: string | null) => {
      if (!subscription?.id) {
        throw new Error("Subscription ID is required");
      }

      await SubscriptionService.reactivateSubscription(subscription.id, newTermStart, newTermEnd);

      if (refresh) {
        await refresh();
      }

      sendNewAlert("Subscription reactivated successfully", AlertSeverity.Success);
    },
    [subscription?.id, refresh, sendNewAlert]
  );

  const handleUpdateEntitlementOverrides = async (overrides: EntitlementConfiguration) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await SubscriptionService.updateEntitlementOverrides(subscription.id, overrides);

    if (refresh) {
      await refresh();
    }

    setLicenseRefreshKey((prev) => prev + 1);

    sendNewAlert("Entitlement overrides updated successfully", AlertSeverity.Success);
  };

  const handleUpdateAutoRenewal = async (autoRenewPeriod: AutoRenewPeriod | null) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await SubscriptionService.updateAutoRenewal(subscription.id, autoRenewPeriod);

    if (refresh) {
      await refresh();
    }

    sendNewAlert("Auto-renewal settings updated successfully", AlertSeverity.Success);
  };

  const handleUpdateZuoraSubscriptionKey = async (zuoraSubscriptionKey: string | null) => {
    if (!subscription?.id) throw new Error("Subscription ID is required");
    await SubscriptionService.updateZuoraSubscriptionKey(subscription.id, zuoraSubscriptionKey);
    if (refresh) await refresh();
    sendNewAlert("Zuora subscription key updated successfully", AlertSeverity.Success);
  };

  const handleUpdateSelfServiceEnabled = async (selfServiceEnabled: boolean) => {
    if (!subscription?.id) throw new Error("Subscription ID is required");
    await SubscriptionService.updateSelfServiceEnabled(subscription.id, selfServiceEnabled);
    if (refresh) await refresh();
    sendNewAlert("Self-service updated successfully", AlertSeverity.Success);
  };

  const handleExtendSubscriptionTerm = async (request: ExtendSubscriptionTermRequestDto) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await SubscriptionService.extendSubscriptionTerm(subscription.id, request);

    if (refresh) {
      await refresh();
    }

    sendNewAlert("Subscription term extended successfully", AlertSeverity.Success);
  };

  const handleTransitionToTrial = async (request: TransitionToTrialRequestDto) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await subscriptionsApi.postV2SubscriptionsByIdTransitionToTrial({ path: { id: subscription.id }, body: request });

    if (refresh) {
      await refresh();
    }

    setLicenseRefreshKey((prev) => prev + 1);
    sendNewAlert("Subscription transitioned to trial successfully", AlertSeverity.Success);
  };

  const handleTransitionToEvergreen = async (request: TransitionToEvergreenRequestDto) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await subscriptionsApi.postV2SubscriptionsByIdTransitionToEvergreen({
      path: { id: subscription.id },
      body: request,
    });

    if (refresh) {
      await refresh();
    }

    setLicenseRefreshKey((prev) => prev + 1);
    sendNewAlert("Subscription transitioned to Free Evergreen successfully", AlertSeverity.Success);
  };

  const handleTransitionToPaid = async (request: TransitionToPaidRequestDto) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await subscriptionsApi.postV2SubscriptionsByIdTransitionToPaid({ path: { id: subscription.id }, body: request });

    if (refresh) {
      await refresh();
    }

    setLicenseRefreshKey((prev) => prev + 1);
    sendNewAlert("Subscription transitioned to paid successfully", AlertSeverity.Success);
  };

  const handleDowngradeToFree = async (request: DowngradeToFreeRequestDto) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await subscriptionsApi.postV2SubscriptionsByIdDowngradeToFree({ path: { id: subscription.id }, body: request });

    if (refresh) {
      await refresh();
    }

    if (isCloudPlatform && subscription.id) refreshCloudSubscription(subscription.id);
    setLicenseRefreshKey((prev) => prev + 1);
    sendNewAlert("Subscription transitioned to Free successfully", AlertSeverity.Success);
  };

  const handleTransitionToComplimentary = async (request: TransitionToComplimentaryRequestDto) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await subscriptionsApi.postV2SubscriptionsByIdTransitionToComplimentary({
      path: { id: subscription.id },
      body: request,
    });

    if (refresh) {
      await refresh();
    }

    sendNewAlert("Subscription transitioned to complimentary successfully", AlertSeverity.Success);
  };

  const handleChangePlan = async (planId: string, clearEntitlementOverrides: boolean) => {
    if (!subscription?.id) {
      throw new Error("Subscription ID is required");
    }

    await SubscriptionService.changeSubscriptionPlan(subscription.id, planId, clearEntitlementOverrides);

    if (refresh) {
      await refresh();
    }

    if (isCloudPlatform) refreshCloudSubscription(subscription.id);

    setLicenseRefreshKey((prev) => prev + 1);

    sendNewAlert("Subscription plan changed successfully", AlertSeverity.Success);
  };

  const handleChangeTaskCap = async (taskCap: string, applyDuringOutageWindow: boolean) => {
    if (!cloudSubscription?.id) throw new Error("Cloud subscription ID is required");
    if (!subscription?.id) throw new Error("Subscription ID is required");

    await changeTaskCap(cloudSubscription.id, taskCap, applyDuringOutageWindow);

    if (lifecycleSyncsTaskCapOverride(subscription.lifecycleType)) {
      const newTaskCap = parseInt(taskCap, 10);
      const taskCapOverride =
        taskCap === UnlimitedTaskCap
          ? { isUnlimited: true, limit: null }
          : isNaN(newTaskCap)
          ? null
          : { isUnlimited: false, limit: newTaskCap };
      await SubscriptionService.updateEntitlementOverrides(subscription.id, {
        ...(subscription.entitlementOverrides ?? {}),
        taskCap: taskCapOverride,
      });
    }

    if (refresh) await refresh();
    if (subscription?.id) refreshCloudSubscription(subscription.id);

    sendNewAlert("Task cap updated successfully", AlertSeverity.Success);
  };

  const subscriptionExtensions = useMemo(() => {
    if (!subscription) return undefined;

    if ("extensions" in subscription && Array.isArray(subscription.extensions)) {
      return subscription.extensions;
    }

    return undefined;
  }, [subscription]);

  const overflowMenuItems = useMemo(() => {
    if (!subscription) {
      return;
    }

    const requiredPolicy = RbacPolicies.Subscriptions.ReadWrite;
    const overflowItems = [];

    if (subscription.id) {
      overflowItems.push(
        Overflow.policyAuthorizedMenuItem(requiredPolicy, "Edit subscription", () =>
          navigate(subscriptionRoutes.detail(subscription.id || "").edit)
        )
      );

      overflowItems.push(
        Overflow.policyAuthorizedMenuItem(RbacPolicies.Subscriptions.Read, "View history", () =>
          navigate(subscriptionRoutes.detail(subscription.id || "").history)
        )
      );

      if (subscription.lifecycleType === LifecycleType.Paid) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(requiredPolicy, "Edit Zuora subscription details", () =>
            setEditZuoraKeyDialogOpen(true)
          )
        );
      }

      if (adoptionLedPricingEnabled) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(requiredPolicy, "Edit self-service", () =>
            setEditSelfServiceDialogOpen(true)
          )
        );
      }

      if (isServerPlatform) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.ServerLicenses.ReadWrite, "Send license email", () =>
            setSendLicenseEmailDialogOpen(true)
          )
        );
      }

      if (lifecycleSupportsEntitlementOverrides(subscription.lifecycleType) && applicableEntitlementKeys.length > 0) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(requiredPolicy, "Edit entitlement overrides", () =>
            setEditOverridesDialogOpen(true)
          )
        );
      }

      const isInfrastructureComplimentary =
        subscription.lifecycleType === "Complimentary" &&
        "complimentaryType" in subscription &&
        subscription.complimentaryType === "Infrastructure";
      if (isInfrastructureComplimentary) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.Admin.ReadWrite, "Edit auto-renewal", () =>
            setEditAutoRenewalDialogOpen(true)
          )
        );
      }

      const canExtendTerm =
        subscription.termEndDate &&
        (subscription.lifecycleType === "Trial" ||
          (subscription.lifecycleType === "Complimentary" &&
            "complimentaryType" in subscription &&
            subscription.complimentaryType !== "Infrastructure")) &&
        (!isCloudPlatform ||
          (cloudSubscription && cloudSubscription.cloudSubscriptionStatus !== CloudSubscriptionStatus.Terminated));

      if (canExtendTerm) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(requiredPolicy, "Extend subscription term", () =>
            setExtendTermDialogOpen(true)
          )
        );
      }

      const canChangeMaintenanceWindow =
        isCloudPlatform &&
        cloudSubscription !== null &&
        cloudSubscription.cloudSubscriptionStatus !== CloudSubscriptionStatus.Terminated;
      if (canChangeMaintenanceWindow) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(
            RbacPolicies.CloudSubscriptions.ReadWrite,
            "Change maintenance window",
            () => setChangeMaintenanceWindowOpen(true)
          )
        );
      }

      const canChangeTaskCap =
        isCloudPlatform &&
        cloudSubscription !== null &&
        (cloudSubscription.cloudSubscriptionStatus === CloudSubscriptionStatus.Subscribed ||
          cloudSubscription.cloudSubscriptionStatus === CloudSubscriptionStatus.Lapsed);
      if (canChangeTaskCap) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.CloudSubscriptions.ReadWrite, "Change task cap", () =>
            setChangeTaskCapDialogOpen(true)
          )
        );
      }

      const canProtectUserAccess =
        isCloudPlatform &&
        cloudSubscription !== null &&
        cloudSubscription.cloudSubscriptionStatus !== CloudSubscriptionStatus.Terminated;
      if (canProtectUserAccess) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.Admin.ReadWrite, "Protect user access", () =>
            setProtectUserAccessDialogOpen(true)
          )
        );
      }

      const canChangePlan =
        subscription.status !== SubscriptionStatus.Canceled &&
        subscription.lifecycleType === LifecycleType.Paid &&
        (isServerPlatform ||
          (isCloudPlatform &&
            cloudSubscription !== null &&
            cloudSubscription.cloudSubscriptionStatus !== CloudSubscriptionStatus.Terminated));
      if (canChangePlan) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(requiredPolicy, "Change plan", () => setChangePlanDialogOpen(true))
        );
      }

      const canTransitionToComplimentary = subscription.lifecycleType !== LifecycleType.Complimentary;
      if (canTransitionToComplimentary) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.Admin.ReadWrite, "Transition to Complimentary", () =>
            setTransitionToComplimentaryDialogOpen(true)
          )
        );
      }

      const canTransitionToTrial = subscription.lifecycleType === LifecycleType.Evergreen;
      if (canTransitionToTrial) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.Admin.ReadWrite, "Transition to Trial", () =>
            setTransitionToTrialDialogOpen(true)
          )
        );
      }

      const canTransitionToEvergreen = subscription.lifecycleType === LifecycleType.Trial;
      if (canTransitionToEvergreen) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.Admin.ReadWrite, "Transition to Free Evergreen", () =>
            setTransitionToEvergreenDialogOpen(true)
          )
        );
      }

      const canTransitionToPaid =
        subscription.lifecycleType !== LifecycleType.Paid &&
        subscription.status !== SubscriptionStatus.Canceled &&
        !isInfrastructureComplimentary;
      if (canTransitionToPaid) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.Admin.ReadWrite, "Transition to Paid", () =>
            setTransitionToPaidDialogOpen(true)
          )
        );
      }

      const canDowngradeToFree =
        subscription.lifecycleType === LifecycleType.Paid && subscription.status !== SubscriptionStatus.Canceled;
      if (canDowngradeToFree) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.Subscriptions.ReadWrite, "Transition to Free", () =>
            setDowngradeToFreeDialogOpen(true)
          )
        );
      }

      const canExpire =
        subscription.status !== SubscriptionStatus.Expired && subscription.status !== SubscriptionStatus.Canceled;
      if (canExpire) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(RbacPolicies.Admin.ReadWrite, "Expire subscription", () =>
            setCancelDialogOpen(true)
          )
        );
      }

      const isExpired = subscription.status === SubscriptionStatus.Expired;
      if (isExpired && reactivatableLifecycleTypes.includes(subscription.lifecycleType)) {
        overflowItems.push(
          Overflow.policyAuthorizedMenuItem(
            RbacPolicies.Subscriptions.ReadWrite,
            "Reactivate subscription",
            async () => {
              if (subscription.lifecycleType === LifecycleType.Evergreen) {
                try {
                  const today = new Date().toISOString().split("T")[0] ?? "";
                  await handleReactivateSubscription(today, null);
                } catch (error) {
                  sendNewAlert(getErrorMessage(error), AlertSeverity.Error);
                }
              } else {
                setReactivateDialogOpen(true);
              }
            }
          )
        );
      }
    }

    return overflowItems;
  }, [
    subscription,
    cloudSubscription,
    isCloudPlatform,
    adoptionLedPricingEnabled,
    applicableEntitlementKeys,
    navigate,
    handleReactivateSubscription,
    sendNewAlert,
  ]);

  if (!subscription) {
    return null;
  }

  const canCreateQuote = adoptionLedPricingEnabled && isAuthorized(RbacPolicies.Orders.ReadWrite);
  const status = subscription.status || "Unknown";
  const platform = subscription.plan?.productPlatform || "Unknown";
  const staffTaskCapExpiry = cloudSubscription?.staffSpecifiedTaskCapExpiryUtc
    ? getOrParseDateTime(cloudSubscription.staffSpecifiedTaskCapExpiryUtc)
    : null;

  const renderPlatformActions = () => {
    if (isServerPlatform) {
      return <ServerSubscriptionActions key="platform-actions" license={serverLicense} loading={platformDataLoading} />;
    }
    if (isCloudPlatform) {
      return (
        <CloudSubscriptionActions
          key="platform-actions"
          cloudSubscription={cloudSubscription}
          loading={platformDataLoading}
        />
      );
    }
    return null;
  };

  function formatDate(dateString: string) {
    return formatDateAsLocaleDateString(DateTime.fromISO(dateString, { zone: "utc" }), false);
  }

  const complimentaryTypeField: React.ReactNode =
    subscription.complimentaryType && subscription.lifecycleType === LifecycleType.Complimentary ? (
      <InfoField label="Complimentary Type" value={subscription.complimentaryType} />
    ) : null;

  const subscriptionForLinkDialog: BillingAccountSubscriptionDto | undefined = serverLicense
    ? {
        id: subscription.id,
        friendlyName: subscription.friendlyName ?? null,
        serial: serverLicense.serial,
        dnsPrefix: null,
        platform: ProductPlatform.OctopusServer,
        planName: subscription.plan?.name ?? subscription.planId,
        status: subscription.status,
        termEndDate: subscription.termEndDate ?? null,
      }
    : cloudSubscription
    ? {
        id: subscription.id,
        friendlyName: subscription.friendlyName ?? null,
        serial: cloudSubscription.serial,
        dnsPrefix: cloudSubscription.dnsPrefix,
        platform: ProductPlatform.OctopusCloud,
        planName: subscription.plan?.name ?? subscription.planId,
        status: subscription.status,
        termEndDate: subscription.termEndDate ?? null,
      }
    : undefined;

  return (
    <>
      <SendLicenseEmailDialog
        open={sendLicenseEmailDialogOpen}
        subscriptionId={subscription.id || ""}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        handleClose={() => setSendLicenseEmailDialogOpen(false)}
        onSent={() => sendNewAlert("License email queued successfully", AlertSeverity.Success)}
      />
      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        subscriptionId={subscription.id || ""}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        handleClose={() => setCancelDialogOpen(false)}
        submitCancel={handleCancelSubscription}
      />
      <ReactivateSubscriptionDialog
        open={reactivateDialogOpen}
        subscriptionId={subscription.id || ""}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        handleClose={() => setReactivateDialogOpen(false)}
        submitReactivate={handleReactivateSubscription}
      />
      <EditEntitlementOverridesDialog
        open={editOverridesDialogOpen}
        subscriptionId={subscription.id || ""}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentOverrides={subscription.entitlementOverrides}
        planInclusions={subscription.plan?.planInclusions}
        handleClose={() => setEditOverridesDialogOpen(false)}
        submitOverrides={handleUpdateEntitlementOverrides}
        isCloudPlatform={isCloudPlatform}
        applicableEntitlementKeys={applicableEntitlementKeys}
      />
      <EditAutoRenewalDialog
        open={editAutoRenewalDialogOpen}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentAutoRenewPeriod={subscription.autoRenewPeriod}
        handleClose={() => setEditAutoRenewalDialogOpen(false)}
        submitAutoRenewal={handleUpdateAutoRenewal}
      />
      <EditZuoraSubscriptionKeyDialog
        open={editZuoraKeyDialogOpen}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentZuoraSubscriptionKey={subscription.zuoraSubscriptionKey}
        handleClose={() => setEditZuoraKeyDialogOpen(false)}
        submitChange={handleUpdateZuoraSubscriptionKey}
      />
      <EditSelfServiceDialog
        open={editSelfServiceDialogOpen}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentValue={subscription.selfServiceEnabled}
        handleClose={() => setEditSelfServiceDialogOpen(false)}
        submitChange={handleUpdateSelfServiceEnabled}
      />
      <ExtendSubscriptionTermDialog
        open={extendTermDialogOpen}
        subscriptionId={subscription.id || ""}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentTermStartDate={subscription.termStartDate}
        currentTermEndDate={subscription.termEndDate}
        extensions={subscriptionExtensions}
        handleClose={() => setExtendTermDialogOpen(false)}
        submitExtension={handleExtendSubscriptionTerm}
      />
      <ChangePlanDialog
        open={changePlanDialogOpen}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentPlan={subscription.plan}
        hasEntitlementOverrides={hasEntitlementOverrides}
        cloudOverridesUnknown={isCloudPlatform && cloudSubscription === null}
        handleClose={() => setChangePlanDialogOpen(false)}
        submitChange={handleChangePlan}
      />
      <TransitionToComplimentaryDialog
        open={transitionToComplimentaryDialogOpen}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentTier={subscription.plan.tier}
        currentLifecycleType={subscription.lifecycleType}
        handleClose={() => setTransitionToComplimentaryDialogOpen(false)}
        submitTransition={handleTransitionToComplimentary}
      />
      <TransitionToTrialDialog
        open={transitionToTrialDialogOpen}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentLifecycleType={subscription.lifecycleType}
        handleClose={() => setTransitionToTrialDialogOpen(false)}
        submitTransition={handleTransitionToTrial}
      />
      <TransitionToEvergreenDialog
        open={transitionToEvergreenDialogOpen}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentLifecycleType={subscription.lifecycleType}
        handleClose={() => setTransitionToEvergreenDialogOpen(false)}
        submitTransition={handleTransitionToEvergreen}
      />
      <TransitionToPaidDialog
        open={transitionToPaidDialogOpen}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentLifecycleType={subscription.lifecycleType}
        platform={subscription.plan.productPlatform}
        handleClose={() => setTransitionToPaidDialogOpen(false)}
        submitTransition={handleTransitionToPaid}
      />
      <DowngradeToFreeDialog
        open={downgradeToFreeDialogOpen}
        subscriptionName={subscription.friendlyName || subscription.id || ""}
        currentLifecycleType={subscription.lifecycleType}
        handleClose={() => setDowngradeToFreeDialogOpen(false)}
        submitDowngrade={handleDowngradeToFree}
      />
      <LinkOrTransferSubscriptionDialog
        open={linkBillingAccountDialogOpen}
        subscription={subscriptionForLinkDialog}
        onCancel={() => setLinkBillingAccountDialogOpen(false)}
        onSaved={() => {
          setLinkBillingAccountDialogOpen(false);
          sendNewAlert("Subscription linked to billing account", AlertSeverity.Success);
          void refresh?.();
        }}
      />
      {cloudSubscription && (
        <ProtectUserAccessDialog
          cloudSubscriptionId={cloudSubscription.id}
          currentValue={cloudSubscription.hasProtectedUserGrants}
          open={protectUserAccessDialogOpen}
          handleClose={() => setProtectUserAccessDialogOpen(false)}
          onSaved={() => {
            if (subscription?.id) refreshCloudSubscription(subscription.id);
            sendNewAlert("User access protection updated successfully", AlertSeverity.Success);
          }}
        />
      )}
      {cloudSubscription && (
        <ChangeMaintenanceWindowDialog
          cloudSubscriptionId={cloudSubscription.id}
          open={changeMaintenanceWindowOpen}
          currentMaintenanceWindow={cloudSubscription.maintenanceWindow}
          handleClose={() => setChangeMaintenanceWindowOpen(false)}
          onSaved={() => {
            if (subscription?.id) refreshCloudSubscription(subscription.id);
            sendNewAlert("Maintenance window updated successfully", AlertSeverity.Success);
          }}
        />
      )}
      {cloudSubscription && (
        <ChangeTaskCapDialog
          cloudSubscriptionId={cloudSubscription.id}
          open={changeTaskCapDialogOpen}
          currentTaskCap={getCurrentTaskCapSelection(cloudSubscription.entitlements.taskCap)}
          handleClose={() => setChangeTaskCapDialogOpen(false)}
          submitChange={handleChangeTaskCap}
        />
      )}
      <CreateQuoteDialog
        open={createQuoteDialogOpen}
        initialTerm={subscription.id}
        onClose={() => setCreateQuoteDialogOpen(false)}
        onCreated={(odNumber) => {
          setCreateQuoteDialogOpen(false);
          openNewQuote(odNumber);
        }}
      />
      <DetailsPageLayout
        pageName={`Subscription ${subscription.friendlyName || subscription.id}`}
        actions={[
          ...(canCreateQuote
            ? [
                <Button key="create-quote" variant="contained" onClick={() => setCreateQuoteDialogOpen(true)}>
                  Create Quote
                </Button>,
              ]
            : []),
          renderPlatformActions(),
        ]}
        overflowMenuItems={overflowMenuItems}
      >
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Fieldset title="Basic Information">
              <InfoField label="Subscription ID" value={subscription.id} />

              {subscription.friendlyName && <InfoField label="Friendly Name" value={subscription.friendlyName} />}

              <Box sx={{ mb: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 0.5,
                  }}
                >
                  Owner
                </Typography>
                {subscription.ownerUser ? (
                  <Box>
                    <Typography variant="body1">
                      {subscription.ownerUser.firstName} {subscription.ownerUser.lastName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      {subscription.ownerUser.email}
                    </Typography>
                  </Box>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    Unknown
                  </Typography>
                )}
              </Box>

              {adoptionLedPricingEnabled && (
                <InfoField
                  label="Billing Account"
                  labelSx={{ mt: 2 }}
                  value={
                    subscription.billingAccountCode ? (
                      isAuthorized(RbacPolicies.BillingAccounts.Read) ? (
                        <MuiLink
                          component={RouterLink}
                          to={billingAccountRoutes.detail(subscription.billingAccountCode).root}
                        >
                          {subscription.billingAccountName ?? subscription.billingAccountCode}
                        </MuiLink>
                      ) : (
                        <Typography variant="body1">
                          {subscription.billingAccountName ?? subscription.billingAccountCode}
                        </Typography>
                      )
                    ) : isAuthorized(RbacPolicies.Admin.ReadWrite) ? (
                      platformDataLoading ? (
                        <CircularProgress size={20} aria-label="Loading billing account" />
                      ) : subscriptionForLinkDialog ? (
                        <Button size="small" onClick={() => setLinkBillingAccountDialogOpen(true)}>
                          Link to Billing Account
                        </Button>
                      ) : (
                        <Typography variant="body1" sx={{ color: "text.secondary" }}>
                          Unable to link — no serial found for this subscription
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body1" sx={{ color: "text.secondary" }}>
                        Not linked
                      </Typography>
                    )
                  }
                  spacing={0}
                />
              )}
            </Fieldset>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Fieldset title="Subscription Details">
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mb: 0.5,
                  }}
                >
                  Status
                </Typography>
                <Chip label={status} color={subscriptionStatusColor(status)} size="small" />
              </Box>

              <InfoField label="Plan" value={subscription.plan?.name || subscription.planId} />

              <InfoField label="Platform" value={formatPlatform(platform)} />

              {adoptionLedPricingEnabled && (
                <InfoField label="Self-Service" value={subscription.selfServiceEnabled ? "Enabled" : "Disabled"} />
              )}

              <InfoField label="Lifecycle Type" value={subscription.lifecycleType || "Unknown"} />

              {complimentaryTypeField}

              {subscription.termStartDate && (
                <InfoField label="Term Start Date" value={formatDate(subscription.termStartDate)} />
              )}

              {subscription.termEndDate && (
                <InfoField label="Term End Date" value={formatDate(subscription.termEndDate)} />
              )}

              {subscription.trialStarted && (
                <InfoField
                  label="Trial Started"
                  value={formatDateAsLocaleDateString(DateTime.fromISO(subscription.trialStarted), false, false)}
                />
              )}

              {subscription.trialFinished && (
                <InfoField label="Trial Finished" value={formatDate(subscription.trialFinished)} />
              )}

              {subscription.expiredOn && <InfoField label="Expired On" value={formatDate(subscription.expiredOn)} />}

              {subscription.lifecycleType === LifecycleType.Complimentary &&
                subscription.complimentaryType === "Infrastructure" && (
                  <InfoField
                    label="Auto-Renewal"
                    value={
                      subscription.autoRenewPeriod !== undefined
                        ? getAutoRenewPeriodLabel(subscription.autoRenewPeriod)
                        : "Disabled"
                    }
                    spacing={0}
                  />
                )}
            </Fieldset>
          </Grid>

          {subscription.plan && (
            <Grid size={12}>
              <EntitlementViewer plan={subscription.plan} entitlementOverrides={subscription.entitlementOverrides} />
            </Grid>
          )}

          {isServerPlatform && serverLicense && (
            <Grid size={12}>
              <Fieldset title="Server License Details">
                {platformDataLoading ? (
                  <Skeleton variant="rectangular" height={100} />
                ) : (
                  <>
                    <InfoField label="License ID" value={serverLicense.id} />
                    <InfoField label="Serial" value={serverLicense.serial} />
                    {serverLicense.validFromDate && (
                      <InfoField
                        label="Valid From"
                        value={formatDateAsLocaleDateString(
                          DateTime.fromISO(serverLicense.validFromDate),
                          false,
                          false
                        )}
                      />
                    )}
                    {serverLicense.expiryDate && (
                      <InfoField
                        label="Valid To"
                        value={formatDateAsLocaleDateString(DateTime.fromISO(serverLicense.expiryDate), false, false)}
                      />
                    )}
                    <InfoField label="Fulfilled" value={serverLicense.isFulfilled ? "Yes" : "No"} spacing={0} />
                  </>
                )}
              </Fieldset>
            </Grid>
          )}

          {isCloudPlatform && cloudSubscription && (
            <Grid size={12}>
              <Fieldset title="Cloud Instance Details">
                {platformDataLoading ? (
                  <Skeleton variant="rectangular" height={100} />
                ) : (
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                      }}
                    >
                      <InfoField label="Instance URL" value={`https://${cloudSubscription.dnsPrefix}.octopus.app`} />
                      <InfoField label="DNS Prefix" value={cloudSubscription.dnsPrefix} />
                      <InfoField label="Region" value={cloudSubscription.cloudRegionId || "Unknown"} />
                      <InfoField
                        label="Instance Provisioned"
                        value={cloudSubscription.isInstanceProvisioned ? "Yes" : "No"}
                      />
                      {cloudSubscription.staticIpPrefixes && cloudSubscription.staticIpPrefixes.length > 0 && (
                        <InfoField label="Static IP Prefixes" value={cloudSubscription.staticIpPrefixes.join(", ")} />
                      )}
                      <InfoField label="Serial" value={cloudSubscription.serial} spacing={0} />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: "text.secondary" }}>
                        Maintenance Window
                      </Typography>
                      <InfoField
                        label="Frequency"
                        value={getMaintenanceWindowFrequencyLabel(
                          cloudSubscription.maintenanceWindow.frequency,
                          cloudSubscription.maintenanceWindow.dayOfWeek,
                          cloudSubscription.maintenanceWindow.weekOfMonth
                        )}
                      />
                      <InfoField
                        label="Time"
                        value={`${formatDateAsTimeString(
                          getOrParseDateTime(cloudSubscription.maintenanceWindow.startTime),
                          cloudSubscription.maintenanceWindow.timeZone,
                          false
                        )} – ${formatDateAsTimeString(
                          getOrParseDateTime(cloudSubscription.maintenanceWindow.endTime),
                          cloudSubscription.maintenanceWindow.timeZone,
                          false
                        )} ${cloudSubscription.maintenanceWindow.timeZone}`}
                        spacing={0}
                      />
                      {cloudSubscription.staffSpecifiedTaskCap !== null &&
                        cloudSubscription.staffSpecifiedTaskCap !== undefined && (
                          <Box sx={{ mt: 3 }}>
                            <InfoField
                              label="Staff-Specified Task Cap"
                              value={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  {formatEntitlementValue(cloudSubscription.staffSpecifiedTaskCap)}
                                  {staffTaskCapExpiry ? (
                                    <>
                                      {` (expires ${formatDateAsLocaleString(staffTaskCapExpiry)})`}
                                      {staffTaskCapExpiry < DateTime.now() && <Chip label="Expired" size="small" />}
                                    </>
                                  ) : (
                                    " (no expiry)"
                                  )}
                                  {lifecycleSyncsTaskCapOverride(subscription.lifecycleType) &&
                                    subscription.entitlementOverrides?.taskCap !== undefined &&
                                    formatEntitlementValue(cloudSubscription.staffSpecifiedTaskCap) !==
                                      formatEntitlementValue(subscription.entitlementOverrides.taskCap) && (
                                      <Chip label="Differs from override" size="small" color="warning" />
                                    )}
                                </Box>
                              }
                              spacing={0}
                            />
                          </Box>
                        )}
                    </Grid>
                  </Grid>
                )}
              </Fieldset>
            </Grid>
          )}

          {isServerPlatform && serverLicense && (
            <Grid size={12}>
              <Fieldset title="License Key XML">
                <LicenseKeyView key={licenseRefreshKey} licenseId={serverLicense.id} />
              </Fieldset>
            </Grid>
          )}

          {subscription.zuoraSubscriptionKey && (
            <Grid size={12}>
              <Fieldset title="Billing System">
                <InfoField
                  label="Zuora Subscription Key"
                  value={
                    <MuiLink
                      href={`${ZuoraUriMap.zuoraSubscriptions}${subscription.zuoraSubscriptionKey}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                      {subscription.zuoraSubscriptionKey} <OpenInNew fontSize="small" />
                    </MuiLink>
                  }
                  spacing={0}
                />
              </Fieldset>
            </Grid>
          )}
        </Grid>
      </DetailsPageLayout>
    </>
  );
}
